import React from 'react'
import { GetServerSideProps } from 'next'
import { SSRData } from 'next-urql'
import Cookies from 'universal-cookie'
import { Client, Provider as UrqlProvider, createClient, ssrExchange } from 'urql'
import { Blog } from 'entities/blog'
import { canSee } from 'entities/blog.auth'
import { UserType } from 'entities/user'
import { headerCache, headerCacheHit, headerCacheMiss } from 'lib/customHeader'
import { isServerSide } from 'lib/env'
import { markdownToHtml, markdownToText } from 'lib/markdown'
import { textToSeoDescription } from 'lib/seo'
import { isUserAuthorized } from 'hocs/withAuth'
import { getUrqlClientConfig } from 'hocs/withGql'
import { getVerticalData } from 'hocs/withVertical'
import { AnalyticsProvider } from 'hooks/useAnalytics'
import { CookiesProvider } from 'hooks/useCookies'
import { MeContext, fetchMe } from 'hooks/useMe'
import { VerticalContext } from 'hooks/useVerticalData'
import {
    BlogForBlogPage,
    BlogForBlogPageExtended,
    QUERY_BLOG,
} from 'containers/blogs/queries'
import { BlogPage } from 'containers/blogs/BlogPage'
import { getExtendedBlogFromCache, putExtendedBlogInCache } from 'containers/blogs/cache'

const authRules = {
    allowedUserTypes: [UserType.Admin, UserType.Company, UserType.Candidate],
}
const cacheOptions = {}

type BlogProps = {
    verticalData: React.ContextType<typeof VerticalContext>
    blog: BlogForBlogPageExtended
    me: React.ContextType<typeof MeContext>
    ssrData: SSRData
    cookies: object
}

const BlogPageWrapped = ({ verticalData, me, blog, ssrData, cookies }: BlogProps) => {
    const client = React.useMemo(() => {
        const ssr = ssrExchange({ isClient: !isServerSide, initialState: ssrData })
        return createClient(getUrqlClientConfig(ssr, undefined, cacheOptions))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <UrqlProvider value={client}>
            <MeContext.Provider value={me}>
                <VerticalContext.Provider value={verticalData}>
                    <CookiesProvider cookiesPlainObject={cookies}>
                        <AnalyticsProvider pageNameForAnalytics={'Blog'}>
                            <BlogPage blog={blog} />
                        </AnalyticsProvider>
                    </CookiesProvider>
                </VerticalContext.Provider>
            </MeContext.Provider>
        </UrqlProvider>
    )
}

export default BlogPageWrapped

export const getServerSideProps: GetServerSideProps<
    BlogProps,
    { id: string }
> = async ctx => {
    const id = ctx.query.id

    if (!id || Array.isArray(id)) {
        return { notFound: true }
    }

    const ssr = ssrExchange({ isClient: !isServerSide })
    const urqlClient = createClient(getUrqlClientConfig(ssr, ctx, cacheOptions))

    const [me, extendedBlogFromCache] = await Promise.all([
        fetchMe(urqlClient, ctx),
        getExtendedBlogFromCache({ id }),
    ])

    if (me && !isUserAuthorized(me, authRules)) {
        return { notFound: true }
    }

    const extendedBlog = await (async () => {
        if (extendedBlogFromCache) {
            updateCacheIfBlogWasUpdated({
                id,
                client: urqlClient,
                blogFromCache: extendedBlogFromCache,
            }).catch(err => {
                console.log('failed to update the cache', err)
            })
            ctx.res.setHeader(headerCache, headerCacheHit)
            return extendedBlogFromCache
        }

        const blog = await getBlogFromDb({ id, client: urqlClient })

        if ('error' in blog) {
            return blog
        }

        const extendedBlog: BlogForBlogPageExtended = {
            ...blog,
            ...(await htmlAndSeoDescription(blog)),
        }

        putExtendedBlogInCache(extendedBlog)
            .catch(err => console.error('Failed to put blog into the cache', id, err))
            .then(() => console.log('blog was saved into the cache'))

        ctx.res.setHeader(headerCache, headerCacheMiss)
        return extendedBlog
    })()

    if ('error' in extendedBlog) {
        console.log(extendedBlog.error)
        return { notFound: true }
    }

    if (!canSee(extendedBlog, me)) {
        return { notFound: true }
    }

    return {
        props: {
            verticalData: getVerticalData(ctx),
            me,
            blog: extendedBlog,
            ssrData: ssr.extractData(),
            cookies: new Cookies(ctx.req?.cookies).getAll(),
        },
    }
}

async function htmlAndSeoDescription(blog: BlogForBlogPage) {
    const blogBody = blog.body

    if (blogBody) {
        return Promise.all([
            markdownToHtml(blogBody),
            markdownToText(blogBody).then(textToSeoDescription),
        ])
            .then(([html, description]) => {
                return { bodyHtml: html, seoDescription: description }
            })
            .catch(err => {
                console.error('Failed to generate blog html or blog SEO description', err)
                return { bodyHtml: '', seoDescription: '' }
            })
    }
    return { bodyHtml: '', seoDescription: '' }
}

async function getBlogFromDb({ id, client }: Pick<Blog, 'id'> & { client: Client }) {
    const blogDataResult = await client
        .query(QUERY_BLOG, { id }, { requestPolicy: 'network-only' })
        .toPromise()

    if (blogDataResult.error) {
        return {
            error: 'Failed to fetch the blog: ' + blogDataResult.error,
        }
    }

    const blog = blogDataResult.data?.blog

    if (!blog) {
        // should not get there by any means, but urql return types...
        return { error: 'The data for blog is missing' }
    }

    return blog
}

async function updateCacheIfBlogWasUpdated({
    id,
    client,
    blogFromCache,
}: Pick<Blog, 'id'> & { client: Client; blogFromCache: BlogForBlogPageExtended }) {
    const blog = await getBlogFromDb({ id, client })

    if ('error' in blog) {
        return blog
    }

    if (blog.updated !== blogFromCache.updated) {
        let extendedBlog = {
            ...blog,
            ...(await htmlAndSeoDescription(blog)),
        }

        await putExtendedBlogInCache(extendedBlog).then(() =>
            console.log('cache was updated'),
        )
    }
}
